import { useTranslation } from '@refinedev/core';
import {
  ActionIcon,
  Badge,
  Box,
  Flex,
  Menu,
  NumberInput,
  Select,
  Text,
  Tooltip,
} from '@mantine/core';
import {
  IconBuildingHospital,
  IconCheck,
  IconCircleCheck,
  IconCircleMinus,
  IconCurrencyDollarOff,
  IconDotsVertical,
  IconMessage,
  IconPencil,
  IconRefresh,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import type { MRT_ColumnDef } from 'mantine-react-table';
import { useEffect, useState } from 'react';
import { type Debit, type EOB, isTariff, type Tariff } from 'interfaces';
import { TariffSearch } from 'components/common/TariffSearch';
import { DiagnosisSearch } from 'components/common/DiagnosisSearch';
import {
  type RowItem,
  formatCad,
  formatPercent,
} from 'components/common/ModalComponents/utils/tariffHelpers';
import {
  getLocationModifierTooltipLabel,
  formatLocationModifierLabel,
} from 'utils/tariffUtils';
import { formatTimeInput } from 'utils/dateUtils';
import { TariffTimeEditor } from 'components/common/ModalComponents/TariffTimeEditor';
import { CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import {
  isResubmittableTariffStatus,
  isWriteOffEligibleTariffStatus,
} from 'utils/claimStatusUtils';
import { getStatusChipPresentation } from 'utils/badgeColors';

export interface TariffModalRow extends RowItem {
  debit?: Debit;
  isPendingResubmission: boolean;
  isResubmitted: boolean;
  stagedAction?: TariffStagedAction;
  requiresReconciliation: boolean;
  hasHospitalCarePremium?: boolean;
  difference: number;
  submittedAmount: number;
}

export type TariffStagedAction =
  | 'writeOff'
  | 'reconcile'
  | 'resubmit'
  | 'markReady'
  | 'markIncomplete';

interface ModalTariffSelectColumnsProps {
  eobHeader: string;
  items: RowItem[];
  providerID?: string;
  isUnreconciled: boolean;
  locationServiceRate: {
    code: string;
    modifier: number;
  };
  isAnesthesiaProvider: boolean;
  editingTotalId: string | null;
  editingStartTimeId: string | null;
  editingEndTimeId: string | null;
  setEditingTotalId: (id: string | null) => void;
  setEditingStartTimeId: (id: string | null) => void;
  setEditingEndTimeId: (id: string | null) => void;
  changeFn: (stringifiedTariff: string, targetRowID: string) => void;
  handleUnitsChange: (rowID: string, newUnits: number) => void;
  handleAnesthesiaUnitsChange: (rowID: string, newUnits: number) => void;
  handleICD9Change: (rowID: string, icd9: string) => void;
  splitOptions: { value: string; label: string }[];
  incisionOptions: { value: string; label: string }[];
  splitLabel: string;
  incisionLabel: string;
  handleSplitChange: (rowID: string, split: string) => void;
  handleIncisionChange: (rowID: string, incision: string | null) => void;
  handlePercentageChange: (rowID: string, newPct: number | undefined) => void;
  handleStartTimeChange: (rowID: string, timeValue: string) => void;
  handleEndTimeChange: (rowID: string, timeValue: string) => void;
  handleManualTotalSave: (rowID: string, value: number | undefined) => boolean;
  handleManualTotalReset: (rowID: string) => void;
  timezone: string;
  colorScheme: string;
}

const numericInputStyles = {
  input: {
    minHeight: 38,
    paddingInline: 8,
    fontVariantNumeric: 'tabular-nums',
  },
};

const MAX_TARIFF_PERCENTAGE = 300;

const hasValidTariff = (tariff?: Partial<Tariff> | null): tariff is Tariff =>
  !!tariff && isTariff(tariff);

const stagedActionLabel: Record<TariffStagedAction, string> = {
  writeOff: 'Pending write off',
  reconcile: 'Pending reconcile',
  resubmit: 'Pending resubmit',
  markReady: 'Pending ready',
  markIncomplete: 'Pending incomplete',
};

const TariffEOBIndicator = ({ eobs = [] }: { eobs?: EOB[] }) => {
  const uniqueEOBs = Array.from(
    new Map(eobs.map((eob) => [eob.code, eob])).values()
  );
  if (!uniqueEOBs.length) return null;

  return (
    <Flex gap={4} wrap="wrap">
      {uniqueEOBs.map((eob) => (
        <Tooltip
          key={eob.code}
          label={eob.description}
          multiline
          w={320}
          withArrow
          position="top"
          events={{ hover: true, focus: true, touch: true }}
        >
          <Badge size="xs" variant="light" color="red" tabIndex={0}>
            {eob.code}
          </Badge>
        </Tooltip>
      ))}
    </Flex>
  );
};

export const ModalTariffSelectColumns = ({
  eobHeader,
  items,
  providerID,
  isUnreconciled,
  locationServiceRate,
  isAnesthesiaProvider,
  editingTotalId,
  editingStartTimeId,
  editingEndTimeId,
  setEditingTotalId,
  setEditingStartTimeId,
  setEditingEndTimeId,
  changeFn,
  handleUnitsChange,
  handleAnesthesiaUnitsChange,
  handleICD9Change,
  splitOptions,
  incisionOptions,
  splitLabel,
  incisionLabel,
  handleSplitChange,
  handleIncisionChange,
  handlePercentageChange,
  handleStartTimeChange,
  handleEndTimeChange,
  handleManualTotalSave,
  handleManualTotalReset,
  timezone,
  colorScheme,
}: ModalTariffSelectColumnsProps): MRT_ColumnDef<TariffModalRow>[] => {
  return [
    {
      accessorKey: 'code',
      header: 'Code',
      size: 132,
      minSize: 104,
      maxSize: 160,
      Cell: ({ row }) => {
        const tariffCode = `${row.original?.prefix ?? ''}${
          row.original?.code ?? ''
        }`;
        const description = row.original?.description
          ? ` - ${row.original.description}`
          : '';
        const tooltipLabel = `${tariffCode}${description}`;
        return (
          <Tooltip label={tooltipLabel} multiline withArrow position="top">
            <Box w="100%">
              <TariffSearch
                dataRowID={row.original._rowID}
                defaultValue={tariffCode}
                onChange={(val) => changeFn(val, row.original._rowID)}
              />
            </Box>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      size: 126,
      minSize: 110,
      maxSize: 150,
      Cell: ({ row }) => {
        const statusLabel = row.original.status?.trim() || 'Unknown';
        const statusChip = getStatusChipPresentation(statusLabel, colorScheme);
        const tariffCode = `${row.original.prefix ?? ''}${
          row.original.code ?? ''
        }`;

        return (
          <Flex direction="column" gap={4} align="flex-start">
            <Badge
              variant="light"
              size="sm"
              radius="xl"
              className="claims-status-badge"
              aria-label={`Tariff ${tariffCode} status: ${statusLabel}`}
              data-testid="tariff-status-badge"
              style={{
                backgroundColor: statusChip.backgroundColor,
                border: `1px solid ${statusChip.borderColor}`,
                color: statusChip.textColor,
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {statusLabel}
            </Badge>
            {row.original.stagedAction ? (
              <Badge
                size="xs"
                variant="light"
                color="yellow"
                aria-label={`${
                  stagedActionLabel[row.original.stagedAction]
                } for ${tariffCode}`}
                data-testid="staged-tariff-action-badge"
                style={{ textTransform: 'none', whiteSpace: 'nowrap' }}
              >
                {stagedActionLabel[row.original.stagedAction]}
              </Badge>
            ) : null}
          </Flex>
        );
      },
    },
    {
      id: 'eobs',
      header: eobHeader,
      size: 76,
      minSize: 64,
      maxSize: 100,
      Cell: ({ row }) => <TariffEOBIndicator eobs={row.original.debit?.eobs} />,
    },
    {
      accessorKey: 'icd9',
      header: 'ICD9',
      size: 108,
      minSize: 88,
      maxSize: 132,
      Cell: ({ row }) => (
        <Box w="100%">
          <DiagnosisSearch
            defaultValue={row.original.icd9 || ''}
            tariff={row.original}
            index={row.index}
            providerID={providerID}
            onChange={(_, icd9) => handleICD9Change(row.original._rowID, icd9)}
          />
        </Box>
      ),
    },
    {
      accessorKey: 'units',
      header: 'Unit',
      size: 74,
      minSize: 62,
      maxSize: 88,
      Cell: ({ row }) => (
        <Box w="100%">
          <NumberInput
            value={row.original.units ?? 1}
            hideControls
            data-testid="units-input"
            allowLeadingZeros={false}
            min={1}
            max={99}
            size="sm"
            w="100%"
            onChange={(val: number | undefined) => {
              if (!val || val < 1) handleUnitsChange(row.original._rowID, 1);
              else handleUnitsChange(row.original._rowID, val);
            }}
            styles={{
              input: {
                ...numericInputStyles.input,
                textAlign: 'left',
              },
            }}
          />
        </Box>
      ),
    },
    {
      id: 'serviceTime',
      header: 'Time',
      size: 142,
      minSize: 120,
      maxSize: 170,
      Cell: ({ row }) => {
        const startTimeValue = formatTimeInput(
          row.original.serviceStart,
          timezone
        );
        const serviceEndValue = formatTimeInput(
          row.original.serviceEnd,
          timezone
        );
        const isEditingStart = editingStartTimeId === row.original._rowID;
        const isEditingEnd = editingEndTimeId === row.original._rowID;

        return (
          <TariffTimeEditor
            rowID={row.original._rowID}
            startTime={startTimeValue}
            endTime={serviceEndValue}
            isEditingStart={isEditingStart}
            isEditingEnd={isEditingEnd}
            setEditingStartTimeId={setEditingStartTimeId}
            setEditingEndTimeId={setEditingEndTimeId}
            handleStartTimeChange={handleStartTimeChange}
            handleEndTimeChange={handleEndTimeChange}
          />
        );
      },
    },
    ...(isAnesthesiaProvider
      ? [
          {
            accessorKey: 'anesthesiaUnits',
            header: 'Anesthesia Units',
            maxSize: 110,
            Cell: ({ row }) => (
              <Box w="100%">
                <NumberInput
                  value={row.original.anesthesiaUnits ?? 0}
                  hideControls
                  data-testid="anesthesia-units-input"
                  allowLeadingZeros={false}
                  min={0}
                  max={96}
                  size="sm"
                  w="100%"
                  onChange={(val: number | undefined) =>
                    handleAnesthesiaUnitsChange(row.original._rowID, val ?? 0)
                  }
                  styles={{
                    input: {
                      ...numericInputStyles.input,
                      textAlign: 'left',
                    },
                  }}
                />
              </Box>
            ),
          } as MRT_ColumnDef<TariffModalRow>,
        ]
      : []),
    {
      accessorKey: 'rate',
      header: 'Base Fee',
      size: 118,
      minSize: 100,
      maxSize: 136,
      Cell: ({ row }) => (
        <Text
          fw={600}
          ta="left"
          style={{ fontVariantNumeric: 'tabular-nums', width: '100%' }}
        >
          {row.original.type === 'Modifier' && formatPercent(row.original.rate)}
          {row.original.type === 'Base' && formatCad(row.original.rate)}
        </Text>
      ),
    },
    {
      accessorKey: 'percentage',
      header: '%',
      size: 72,
      minSize: 64,
      maxSize: 88,
      Cell: ({ row }) => (
        <Box w="100%">
          <NumberInput
            value={row.original.percentage ?? 100}
            allowLeadingZeros={false}
            max={MAX_TARIFF_PERCENTAGE}
            min={0}
            hideControls
            suffix="%"
            disabled={row.original.type === 'Modifier'}
            data-testid="percentage-input"
            onChange={(val: number | undefined) =>
              handlePercentageChange(row.original._rowID, val)
            }
            w="100%"
            size="sm"
            styles={{
              input: {
                ...numericInputStyles.input,
                textAlign: 'left',
                paddingRight: 22,
              },
            }}
          />
        </Box>
      ),
    },
    ...(!isAnesthesiaProvider
      ? ([
          {
            accessorKey: 'split',
            header: splitLabel,
            size: 116,
            minSize: 100,
            Cell: ({ row }) => (
              <Select
                aria-label={splitLabel}
                data={splitOptions}
                value={row.original.split || ' '}
                onChange={(value) =>
                  handleSplitChange(row.original._rowID, value ?? ' ')
                }
                size="xs"
                radius="md"
                data-testid="split-select-row"
              />
            ),
          },
          {
            accessorKey: 'incision',
            header: incisionLabel,
            size: 116,
            minSize: 100,
            Cell: ({ row }) => (
              <Select
                aria-label={incisionLabel}
                data={incisionOptions}
                value={row.original.incision ?? ''}
                onChange={(value) =>
                  handleIncisionChange(row.original._rowID, value || null)
                }
                size="xs"
                radius="md"
                data-testid="incision-select-row"
              />
            ),
          },
        ] satisfies MRT_ColumnDef<TariffModalRow>[])
      : []),
    {
      accessorKey: 'total',
      header: 'Submitted',
      size: 132,
      minSize: 112,
      maxSize: 160,
      Cell: ({ row }) => (
        <SubmittedAmountCell
          row={row}
          items={items}
          locationServiceRate={locationServiceRate}
          isUnreconciled={isUnreconciled}
          editingTotalId={editingTotalId}
          setEditingTotalId={setEditingTotalId}
          handleManualTotalSave={handleManualTotalSave}
          handleManualTotalReset={handleManualTotalReset}
        />
      ),
    },
    {
      id: 'paidAmount',
      header: 'Paid',
      size: 104,
      minSize: 88,
      maxSize: 130,
      accessorFn: (row) => Number(row.debit?.paidAmount ?? 0),
      Cell: ({ row }) => (
        <Text fw={600}>{formatCad(row.original.debit?.paidAmount ?? 0)}</Text>
      ),
    },
    {
      accessorKey: 'difference',
      header: 'Difference',
      size: 120,
      minSize: 100,
      maxSize: 150,
      Cell: ({ row }) => (
        <Text
          size="sm"
          c="var(--mantine-color-text)"
          data-testid="tariff-difference"
          fw={600}
        >
          ${row.original.difference.toFixed(2)}
        </Text>
      ),
    },
  ];
};
export const TariffRowActions = ({
  row,
  isUnreconciled,
  onReconcile,
  onComment,
  onWriteOff,
  onDelete,
  onResubmit,
  onMarkAs,
}: {
  row: TariffModalRow;
  isUnreconciled: boolean;
  onReconcile: () => Promise<void> | void;
  onComment?: () => void;
  onWriteOff: () => Promise<void>;
  onDelete: () => void;
  onResubmit: () => Promise<void>;
  onMarkAs: (status: string) => Promise<void> | void;
}) => {
  const { translate } = useTranslation();
  const isPending = row.status === CLAIM_TARIFF_STATUS.PENDING;
  const canResubmit =
    hasValidTariff(row) && isResubmittableTariffStatus(row.status);
  const canReconcile =
    row.status === CLAIM_TARIFF_STATUS.REJECTED ||
    (row.requiresReconciliation && isUnreconciled);
  const canWriteOff =
    isWriteOffEligibleTariffStatus(row.status) && Boolean(row.debit);

  return (
    <Flex justify="center" w="100%" align="center" gap={4}>
      {onComment && (
        <Tooltip
          label={translate(
            'pages:claims.tariffComment.open',
            'Comment to Provider'
          )}
        >
          <ActionIcon
            variant="subtle"
            color={row.pendingComment || row.latestComment ? 'teal' : 'gray'}
            aria-label={translate(
              'pages:claims.tariffComment.open',
              'Comment to Provider'
            )}
            disabled={!hasValidTariff(row)}
            onClick={onComment}
          >
            <IconMessage size={17} />
          </ActionIcon>
        </Tooltip>
      )}
      {row.isResubmitted && (
        <Badge
          size="sm"
          variant="light"
          color="yellow"
          style={{ textTransform: 'uppercase' }}
        >
          Resubmitted
        </Badge>
      )}
      <Menu position="bottom-end" withinPortal>
        <Menu.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            aria-label="Row actions"
            disabled={isPending}
            title={
              isPending
                ? translate('pages:claims.pendingActionDisabled')
                : undefined
            }
          >
            <IconDotsVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<IconCircleCheck size={16} />}
            disabled={!hasValidTariff(row)}
            onClick={() => onMarkAs(CLAIM_TARIFF_STATUS.READY)}
          >
            Mark as ready
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCircleMinus size={16} />}
            disabled={!hasValidTariff(row)}
            onClick={() => onMarkAs(CLAIM_TARIFF_STATUS.INCOMPLETE)}
          >
            Mark as incomplete
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCheck size={16} />}
            disabled={!canReconcile}
            onClick={() => void onReconcile()}
          >
            {translate('pages:claims.actions.reconcile', 'Reconcile')}
          </Menu.Item>
          <Menu.Item
            leftSection={<IconCurrencyDollarOff size={16} />}
            disabled={!canWriteOff}
            onClick={() => void onWriteOff()}
          >
            Write off tariff
          </Menu.Item>
          <Menu.Item
            leftSection={<IconRefresh size={16} />}
            disabled={!canResubmit}
            onClick={() => void onResubmit()}
          >
            Resubmit tariff
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            leftSection={<IconTrash size={16} />}
            color="red"
            disabled={isUnreconciled}
            onClick={onDelete}
          >
            Delete
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Flex>
  );
};

type SubmittedAmountCellProps = Pick<
  ModalTariffSelectColumnsProps,
  | 'items'
  | 'isUnreconciled'
  | 'locationServiceRate'
  | 'editingTotalId'
  | 'setEditingTotalId'
  | 'handleManualTotalSave'
  | 'handleManualTotalReset'
> & {
  row: { original: TariffModalRow };
};

const getSubmittedAmountDraftValue = (
  item: RowItem | undefined,
  rowTotal: number | undefined
): string | number => {
  const total = item?.isManualTotal ? item.total ?? rowTotal : rowTotal;

  return total === 0 || total === undefined ? '' : total;
};

const SubmittedAmountCell = ({
  row,
  items,
  locationServiceRate,
  isUnreconciled,
  editingTotalId,
  setEditingTotalId,
  handleManualTotalSave,
  handleManualTotalReset,
}: SubmittedAmountCellProps) => {
  const canEditManualTotal =
    !isUnreconciled && hasValidTariff(row.original as Partial<Tariff>);
  const isModifier = row.original.type === 'Modifier';
  const shouldApplyLocationModifier =
    row.original.applyLocationModifier !== false;
  const locationModifierLabel =
    formatLocationModifierLabel(locationServiceRate);
  const item = items.find((i) => i._rowID === row.original._rowID);
  const [draftValue, setDraftValue] = useState<string | number>(() =>
    getSubmittedAmountDraftValue(item, row.original.total)
  );

  useEffect(() => {
    setDraftValue(getSubmittedAmountDraftValue(item, row.original.total));
  }, [item?._rowID, item?.isManualTotal, item?.total, row.original.total]);

  if (editingTotalId === row.original._rowID && !isUnreconciled) {
    return (
      <Flex justify="space-between" align="center" gap={8} w="100%">
        <NumberInput
          data-testid="submitted-amount-input"
          value={draftValue}
          allowLeadingZeros={false}
          min={0}
          step={1}
          hideControls
          size="sm"
          flex={1}
          onChange={(val: string | number) => setDraftValue(val)}
          styles={{
            input: {
              ...numericInputStyles.input,
              textAlign: 'right',
            },
          }}
        />
        <Flex align="center" gap={4}>
          <ActionIcon
            aria-label="Save total"
            variant="subtle"
            color="green"
            radius="md"
            onClick={() => {
              const didSave = handleManualTotalSave(
                row.original._rowID,
                draftValue === '' ? undefined : Number(draftValue)
              );
              if (didSave) {
                setEditingTotalId(null);
              }
            }}
          >
            <IconCheck size={16} />
          </ActionIcon>
          <ActionIcon
            aria-label="Cancel edit"
            variant="subtle"
            color="gray"
            radius="md"
            onClick={() => {
              setDraftValue(
                getSubmittedAmountDraftValue(item, row.original.total)
              );
              setEditingTotalId(null);
            }}
          >
            <IconX size={16} />
          </ActionIcon>
          {item?.isManualTotal && (
            <Tooltip label="Reset to calculated" withArrow position="top">
              <ActionIcon
                aria-label="Reset manual total"
                variant="subtle"
                color="yellow"
                radius="md"
                onClick={() => {
                  handleManualTotalReset(row.original._rowID);
                  setDraftValue(
                    getSubmittedAmountDraftValue(item, row.original.total)
                  );
                  setEditingTotalId(null);
                }}
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex
      justify="flex-end"
      direction={'column'}
      align="center"
      w="100%"
      style={{ cursor: canEditManualTotal ? 'pointer' : 'default' }}
      data-testid={canEditManualTotal ? 'Edit total' : undefined}
      onClick={() => {
        if (canEditManualTotal) {
          setDraftValue(getSubmittedAmountDraftValue(item, row.original.total));
          setEditingTotalId(row.original._rowID);
        }
      }}
      gap="sm"
    >
      <Flex justify="center" align="center" w="100%" gap="sm">
        <Tooltip
          label={
            item?.isManualTotal
              ? 'Manual override applied. Click pencil to edit or reset.'
              : 'Click to manually override total'
          }
          disabled={!canEditManualTotal}
          withArrow
          position="top"
        >
          <Text
            fw={600}
            data-testid="total-fee-text"
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formatCad(row.original.submittedAmount)}
          </Text>
        </Tooltip>
        {row.original.hasHospitalCarePremium && (
          <Tooltip
            label="Hospital care premium applied"
            withArrow
            position="top"
          >
            <Box component="span">
              <IconBuildingHospital
                size={16}
                color="var(--mantine-color-blue-6)"
              />
            </Box>
          </Tooltip>
        )}
        {canEditManualTotal && (
          <IconPencil size={14} color="var(--mantine-color-dimmed)" />
        )}
      </Flex>
      {!isModifier && shouldApplyLocationModifier && (
        <Tooltip
          label={getLocationModifierTooltipLabel(locationServiceRate)}
          withArrow
          position="top"
        >
          <Text
            size="xs"
            c="dimmed"
            ta="center"
            w="100%"
            data-testid="location-differential-text"
          >
            {locationModifierLabel}
          </Text>
        </Tooltip>
      )}
    </Flex>
  );
};
